import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import {
  fetchParentStudents,
  getActiveStudentId,
  setActiveStudentId
} from '../lib/parentStudents'

export default function Settings({ theme, setTheme }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [role, setRole] = useState(null)
  const [profile, setProfile] = useState(null)
  const [student, setStudent] = useState(null)
  const [parentStudents, setParentStudents] = useState([])
  const [showPwModal, setShowPwModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  const [newPw, setNewPw] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [toast, setToast] = useState('')

  useEffect(() => {
    fetchProfile()
  }, [])

  function showToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(''), 2500)
  }

  async function fetchProfile() {
    setLoading(true)

    const { data: { user } } =
      await supabase.auth.getUser()

    if (!user) {
      setLoading(false)
      return
    }

    const { data: t } =
      await supabase
        .from('teachers')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

    if (t) {
      setRole(
        t.role === 'assistant'
          ? 'assistant'
          : 'teacher'
      )

      setProfile(t)
      setLoading(false)
      return
    }

    const { data: p } =
      await supabase
        .from('parents')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

    if (p) {
      const { students } =
        await fetchParentStudents(user.id)

      const activeId =
        getActiveStudentId(students)

      setRole('parent')
      setProfile(p)
      setParentStudents(students)

      setStudent(
        students.find(
          s => s.id === activeId
        ) || null
      )

      setLoading(false)
      return
    }

    const { data: s } =
      await supabase
        .from('student_accounts')
        .select(
          '*, students(name, student_code, grade, groups(name, day, time))'
        )
        .eq('id', user.id)
        .maybeSingle()

    if (s) {
      setRole('student')
      setProfile(s)
      setStudent(s.students)
      setLoading(false)
      return
    }

    setLoading(false)
  }

  function switchParentStudent(studentId) {
    const selected =
      parentStudents.find(
        s => s.id === studentId
      )

    if (!selected) return

    setActiveStudentId(studentId)
    setStudent(selected)

    showToast(
      `Now viewing ${selected.name} ✓`
    )
  }

  async function updatePhone() {
    if (!newPhone.trim()) return

    setSaving(true)

    const { data: { user } } =
      await supabase.auth.getUser()

    const table =
      role === 'student'
        ? 'student_accounts'
        : role === 'parent'
          ? 'parents'
          : 'teachers'

    const { error } =
      await supabase
        .from(table)
        .update({
          phone: newPhone.trim()
        })
        .eq('id', user.id)

    if (error) {
      showToast('Error: ' + error.message)
      setSaving(false)
      return
    }

    setProfile(prev => ({
      ...prev,
      phone: newPhone.trim()
    }))

    setNewPhone('')
    setShowPhoneModal(false)

    showToast('Phone number updated ✓')

    setSaving(false)
  }

  async function updateEmail() {
    if (!newEmail.trim()) return

    setSaving(true)

    const cleanEmail =
      newEmail.trim().toLowerCase()

    const { error } =
      await supabase.auth.updateUser({
        email: cleanEmail
      })

    if (error) {
      showToast('Error: ' + error.message)
      setSaving(false)
      return
    }

    const { data: { user } } =
      await supabase.auth.getUser()

    const table =
      role === 'student'
        ? 'student_accounts'
        : role === 'parent'
          ? 'parents'
          : 'teachers'

    await supabase
      .from(table)
      .update({
        email: cleanEmail
      })
      .eq('id', user.id)

    setProfile(prev => ({
      ...prev,
      email: cleanEmail
    }))

    setNewEmail('')
    setShowEmailModal(false)

    showToast('Email updated ✓')

    setSaving(false)
  }

  async function updatePassword() {
    if (!newPw || newPw.length < 6) {
      showToast('Min 6 characters')
      return
    }

    setSaving(true)

    const { error } =
      await supabase.auth.updateUser({
        password: newPw
      })

    if (error) {
      showToast('Error: ' + error.message)
      setSaving(false)
      return
    }

    setNewPw('')
    setShowPwModal(false)

    showToast('Password updated ✓')

    setSaving(false)
  }

  async function signOut() {
    if (!window.confirm('Sign out?')) return

    await supabase.auth.signOut()
  }

  async function deleteAccount() {
    if (!window.confirm(
      'Delete your account permanently? This cannot be undone.'
    )) {
      return
    }

    const { data: { user } } =
      await supabase.auth.getUser()

    if (
      role === 'teacher' ||
      role === 'assistant'
    ) {
      await supabase
        .from('teachers')
        .delete()
        .eq('id', user.id)

    } else if (role === 'parent') {
      await supabase
        .from('parents')
        .delete()
        .eq('id', user.id)

    } else if (role === 'student') {
      await supabase
        .from('student_accounts')
        .delete()
        .eq('id', user.id)
    }

    try {
      await supabase.rpc('delete_user')
    } catch (e) {}

    await supabase.auth.signOut()
  }

  const roleConfig = {
    teacher: {
      label: 'Teacher',
      icon: '👩‍🏫'
    },
    assistant: {
      label: 'Assistant',
      icon: '🧑‍💼'
    },
    parent: {
      label: 'Parent',
      icon: '👨‍👩‍👦'
    },
    student: {
      label: 'Student',
      icon: '🎓'
    },
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 300
      }}>
        <div style={{
          width: 28,
          height: 28,
          border: '3px solid var(--border)',
          borderTopColor: 'var(--accent)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />

        <style>
          {`@keyframes spin {
            to { transform: rotate(360deg) }
          }`}
        </style>
      </div>
    )
  }

  const ModalWrap = ({
    show,
    onClose,
    title,
    children
  }) =>
    !show
      ? null
      : (
        <div
          className="overlay"
          onClick={e =>
            e.target === e.currentTarget &&
            onClose()
          }
        >
          <div className="modal">
            <div className="modal-title">
              {title}
            </div>

            {children}
          </div>
        </div>
      )

  const {
    label: roleLabel,
    icon: roleIcon
  } = roleConfig[role] || {}

  return (
    <div style={{ maxWidth: 560 }}>

      <h2 style={{
        fontSize: 20,
        fontWeight: 500,
        marginBottom: 20
      }}>
        Settings
      </h2>

      {/* Profile */}
      <div
        className="card"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16
        }}
      >
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'var(--accent-bg)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 600,
          flexShrink: 0
        }}>
          {profile?.name
            ?.charAt(0)
            ?.toUpperCase()}
        </div>

        <div style={{
          flex: 1,
          minWidth: 0
        }}>
          <div style={{
            fontSize: 17,
            fontWeight: 600,
            color: 'var(--text)'
          }}>
            {profile?.name}
          </div>

          <div style={{
            fontSize: 13,
            color: 'var(--text3)',
            marginTop: 2
          }}>
            {roleIcon} {roleLabel}
          </div>

          <div style={{
            fontSize: 12,
            color: 'var(--text3)',
            marginTop: 2,
            overflowWrap: 'anywhere'
          }}>
            {profile?.email}
          </div>

          {profile?.phone && (
            <div style={{
              fontSize: 12,
              color: 'var(--accent)',
              marginTop: 2
            }}>
              📞 {profile.phone}
            </div>
          )}
        </div>
      </div>

      {/* Parent Students */}
      {role === 'parent' && (
        <div
          className="card"
          style={{ marginBottom: 16 }}
        >
          <div style={{
            fontSize: 13,
            color: 'var(--text3)',
            marginBottom: 4
          }}>
            My Children
          </div>

          <div style={{
            fontSize: 12,
            color: 'var(--text3)',
            marginBottom: 12
          }}>
            Choose a student to view their
            grades, attendance, homework and
            reports.
          </div>

          {parentStudents.length === 0 ? (
            <div style={{
              fontSize: 13,
              color: 'var(--danger)'
            }}>
              No students linked to this account.
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}>
              {parentStudents.map(s => {
                const active =
                  student?.id === s.id

                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() =>
                      switchParentStudent(s.id)
                    }
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '11px 12px',
                      borderRadius: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                      border:
                        `1px solid ${
                          active
                            ? 'var(--accent)'
                            : 'var(--border)'
                        }`,
                      background:
                        active
                          ? 'var(--accent-bg)'
                          : 'var(--surface)',
                      color: 'var(--text)'
                    }}
                  >
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'var(--accent-bg)',
                      color: 'var(--accent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0
                    }}>
                      {s.name
                        ?.charAt(0)
                        ?.toUpperCase()}
                    </div>

                    <div style={{
                      flex: 1,
                      minWidth: 0
                    }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: 14
                      }}>
                        {s.name}
                      </div>

                      <div style={{
                        fontSize: 12,
                        color: 'var(--text3)',
                        marginTop: 2
                      }}>
                        {s.grade || '—'} ·{' '}
                        <span style={{
                          fontFamily: 'monospace'
                        }}>
                          {s.student_code}
                        </span>
                      </div>
                    </div>

                    <span style={{
                      fontSize: 12,
                      color:
                        active
                          ? 'var(--accent)'
                          : 'var(--text3)',
                      fontWeight: 600
                    }}>
                      {active
                        ? '✓ Current'
                        : 'View'}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Student Account Profile */}
      {role === 'student' &&
        student && (
          <div
            className="card"
            style={{ marginBottom: 16 }}
          >
            <div style={{
              fontSize: 13,
              color: 'var(--text3)',
              marginBottom: 12
            }}>
              My Student Profile
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 600,
                flexShrink: 0
              }}>
                {student.name
                  ?.charAt(0)
                  ?.toUpperCase()}
              </div>

              <div style={{
                flex: 1,
                minWidth: 0
              }}>
                <div style={{
                  fontWeight: 500,
                  color: 'var(--text)'
                }}>
                  {student.name}
                </div>

                {student.grade && (
                  <div style={{
                    fontSize: 12,
                    color: 'var(--text3)',
                    marginTop: 2
                  }}>
                    📚 {student.grade}
                  </div>
                )}

                {student.groups && (
                  <div style={{
                    fontSize: 12,
                    color: 'var(--text3)',
                    marginTop: 2
                  }}>
                    📅 {student.groups.name}
                    {student.groups.day &&
                      ` · ${student.groups.day}`}
                    {student.groups.time &&
                      ` · ${student.groups.time}`}
                  </div>
                )}
              </div>

              <div style={{
                background: 'var(--accent-bg)',
                borderRadius: 10,
                padding: '8px 14px',
                textAlign: 'center',
                flexShrink: 0
              }}>
                <div style={{
                  fontSize: 10,
                  color: 'var(--accent)',
                  marginBottom: 2
                }}>
                  Code
                </div>

                <div style={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: 'var(--accent)',
                  fontSize: 16,
                  letterSpacing: 1
                }}>
                  {student.student_code}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Appearance */}
      <div
        className="card"
        style={{ marginBottom: 16 }}
      >
        <div style={{
          fontSize: 13,
          color: 'var(--text3)',
          marginBottom: 12
        }}>
          Appearance
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{
              fontWeight: 500,
              color: 'var(--text)',
              fontSize: 14
            }}>
              Dark Mode
            </div>

            <div style={{
              fontSize: 12,
              color: 'var(--text3)',
              marginTop: 1
            }}>
              {theme === 'dark' ? 'On' : 'Off'}
            </div>
          </div>

          <button
            onClick={() =>
              setTheme(t =>
                t === 'light'
                  ? 'dark'
                  : 'light'
              )
            }
            style={{
              width: 48,
              height: 28,
              borderRadius: 14,
              border: 'none',
              cursor: 'pointer',
              background:
                theme === 'dark'
                  ? 'var(--accent)'
                  : 'var(--border)',
              position: 'relative',
              transition: '.2s',
              flexShrink: 0
            }}
          >
            <div style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#fff',
              position: 'absolute',
              top: 3,
              transition: '.2s',
              left:
                theme === 'dark'
                  ? 23
                  : 3,
              boxShadow:
                '0 1px 3px rgba(0,0,0,.2)'
            }} />
          </button>
        </div>
      </div>

      {/* Account Settings */}
      <div
        className="card"
        style={{
          marginBottom: 16,
          padding: 0,
          overflow: 'hidden'
        }}
      >
        <div style={{
          fontSize: 13,
          color: 'var(--text3)',
          padding: '14px 20px 8px'
        }}>
          Account Settings
        </div>

        {[
          {
            icon: '📞',
            title: 'Phone Number',
            sub:
              profile?.phone ||
              'Not set',
            onClick: () =>
              setShowPhoneModal(true)
          },
          {
            icon: '✉️',
            title: 'Email Address',
            sub:
              profile?.email || '—',
            onClick: () =>
              setShowEmailModal(true)
          },
          {
            icon: '🔒',
            title: 'Change Password',
            sub:
              'Update your password',
            onClick: () =>
              setShowPwModal(true)
          },
        ].map((row, i) => (
          <button
            key={i}
            onClick={row.onClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '13px 20px',
              width: '100%',
              background: 'none',
              border: 'none',
              borderTop:
                '0.5px solid var(--border)',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background .15s'
            }}
            onMouseEnter={e =>
              e.currentTarget.style.background =
                'var(--bg2)'
            }
            onMouseLeave={e =>
              e.currentTarget.style.background =
                'none'
            }
          >
            <span style={{ fontSize: 20 }}>
              {row.icon}
            </span>

            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 500,
                color: 'var(--text)',
                fontSize: 14
              }}>
                {row.title}
              </div>

              <div style={{
                fontSize: 12,
                color: 'var(--text3)',
                marginTop: 1,
                overflowWrap: 'anywhere'
              }}>
                {row.sub}
              </div>
            </div>

            <span style={{
              color: 'var(--text3)',
              fontSize: 22,
              lineHeight: 1
            }}>
              ›
            </span>
          </button>
        ))}
      </div>

      <button
        onClick={signOut}
        style={{
          width: '100%',
          padding: 13,
          borderRadius: 12,
          border:
            '0.5px solid var(--border)',
          background: 'var(--surface)',
          color: 'var(--danger)',
          fontSize: 15,
          fontWeight: 500,
          cursor: 'pointer',
          marginBottom: 10
        }}
      >
        Sign Out
      </button>

      <button
        onClick={deleteAccount}
        style={{
          width: '100%',
          padding: 13,
          borderRadius: 12,
          border:
            '0.5px solid var(--danger)',
          background: 'var(--danger-bg)',
          color: 'var(--danger)',
          fontSize: 15,
          fontWeight: 500,
          cursor: 'pointer',
          marginBottom: 40
        }}
      >
        Delete Account
      </button>

      {/* Phone Modal */}
      <ModalWrap
        show={showPhoneModal}
        onClose={() =>
          setShowPhoneModal(false)
        }
        title="Update Phone Number"
      >
        <div className="field">
          <label>
            New Phone Number
          </label>

          <input
            value={newPhone}
            onChange={e =>
              setNewPhone(e.target.value)
            }
            placeholder="01xxxxxxxxx"
            type="tel"
            autoFocus
          />
        </div>

        <div className="modal-actions">
          <button
            className="btn"
            onClick={() =>
              setShowPhoneModal(false)
            }
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={updatePhone}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </ModalWrap>

      {/* Email Modal */}
      <ModalWrap
        show={showEmailModal}
        onClose={() =>
          setShowEmailModal(false)
        }
        title="Update Email"
      >
        <div className="field">
          <label>
            New Email Address
          </label>

          <input
            value={newEmail}
            onChange={e =>
              setNewEmail(e.target.value)
            }
            placeholder="new@email.com"
            type="email"
            autoFocus
          />
        </div>

        <div className="modal-actions">
          <button
            className="btn"
            onClick={() =>
              setShowEmailModal(false)
            }
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={updateEmail}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </ModalWrap>

      {/* Password Modal */}
      <ModalWrap
        show={showPwModal}
        onClose={() =>
          setShowPwModal(false)
        }
        title="Change Password"
      >
        <div className="field">
          <label>New Password</label>

          <input
            value={newPw}
            onChange={e =>
              setNewPw(e.target.value)
            }
            placeholder="Min 6 characters"
            type="password"
            autoFocus
          />
        </div>

        <div className="modal-actions">
          <button
            className="btn"
            onClick={() =>
              setShowPwModal(false)
            }
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={updatePassword}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </ModalWrap>

      {toast && (
        <div className="toast">
          {toast}
        </div>
      )}
    </div>
  )
}